import { useState } from 'react';
import { addDays, differenceInCalendarDays } from 'date-fns';
import { Search } from 'lucide-react';
import { Button } from '@shared/ui/primitives/Button';
import { TextInputField } from '@shared/ui/forms/FormFields';
import { cn } from '@shared/lib/cn';
import { createBooking } from '../memberAppApi';
import { isoDay, startOfToday } from '../visitorAccessFormat';
import { FIELD, INPUT, SEGMENTS, chipClass, segmentClass } from '../visitorAccessStyles';
import { VisitDates } from '../VisitDates';
import { BookingField, BookingSteps, PropertyField, useCreatePin, type BookingProps } from './BookingForm';
import { CourierSheet } from './CourierSheet';
import { normalizePhone } from '../PersonSheet';

/** The gate keeps a delivery PIN for up to 30 days, so the window can be at most that long. */
const MAX_WINDOW_DAYS = 30;

const KINDS = [
  { value: 'delivery', label: 'Delivery' },
  { value: 'collection', label: 'Collection' },
] as const;

const STEPS = [
  { label: 'Courier', title: "What's coming?", description: "Pick the courier. If yours isn't listed, tap Other." },
  { label: 'When', title: 'When are they coming?', description: `The PIN works on any day in this window, up to ${MAX_WINDOW_DAYS} days.` },
  { label: 'More', title: 'Anything else?', description: 'Both are optional. They help the guard match the driver.' },
];

/** A PIN for a courier dropping off or picking up, any day in the window the member expects them. */
export function DeliveryBooking({ properties, onCreated, lead }: BookingProps): JSX.Element {
  // 1. Starts as a delivery expected today, at the member's first property.
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<(typeof KINDS)[number]['value']>('delivery');
  const [locationId, setLocationId] = useState(properties[0]!.location_id);
  const [courier, setCourier] = useState('');
  const [from, setFrom] = useState(startOfToday);
  const [to, setTo] = useState(startOfToday);
  const [reference, setReference] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const mutation = useCreatePin(createBooking, onCreated);

  // 2. The property's own couriers lead; one picked from the gate's full list joins them.
  const featured = properties.find((property) => property.location_id === locationId)!.features.featured_couriers;
  const chips = !courier || featured.includes(courier) ? featured : [...featured, courier];

  // 3. How many days the window runs, both ends counted.
  const windowDays = differenceInCalendarDays(to, from) + 1;

  const submit = () =>
    mutation.mutate({
      kind,
      location_id: locationId,
      start_date: isoDay(from),
      end_date: isoDay(to),
      courier,
      reference: reference.trim() || undefined,
      driver_phone: normalizePhone(driverPhone) || undefined,
    });

  return (
    <BookingSteps
      lead={lead}
      steps={STEPS}
      step={step}
      onStep={setStep}
      action={
        step < STEPS.length - 1 ? (
          <Button width="full" disabled={!courier} onClick={() => setStep(step + 1)}>
            Continue
          </Button>
        ) : (
          <Button width="full" loading={mutation.isPending} onClick={submit}>
            Create PIN
          </Button>
        )
      }
    >
      {/* 4. Courier: delivery or collection, and who is coming. */}
      {step === 0 ? (
        <>
          <div role="group" aria-label="Delivery or collection" className={cn(SEGMENTS, 'grid-cols-2 bg-sunken')}>
            {KINDS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={option.value === kind}
                className={segmentClass(option.value === kind)}
                onClick={() => setKind(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <BookingField label="Courier">
            <div className="flex flex-wrap gap-2">
              {chips.map((name) => (
                <button key={name} type="button" aria-pressed={name === courier} className={chipClass(name === courier)} onClick={() => setCourier(name)}>
                  {name}
                </button>
              ))}
              <button type="button" className={cn(chipClass(false), 'flex items-center gap-1.5')} onClick={() => setSearchOpen(true)}>
                <Search className="h-3.5 w-3.5" strokeWidth={2} />
                Other
              </button>
            </div>
          </BookingField>
        </>
      ) : null}

      {/* 5. When: the window they may come in. */}
      {step === 1 ? (
        <VisitDates
          dayLabel="From"
          start={from}
          onStart={(day) => {
            setFrom(day);
            setTo(addDays(day, windowDays - 1));
          }}
          length={{ unit: 'day', value: windowDays, max: MAX_WINDOW_DAYS, onChange: (value) => setTo(addDays(from, value - 1)) }}
        />
      ) : null}

      {/* 6. More: what helps the guard, and the property. */}
      {step === 2 ? (
        <>
          <TextInputField
            label="Order or reference number"
            placeholder="Optional"
            className={FIELD}
            inputClassName={INPUT}
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
          <TextInputField
            label="Driver's mobile"
            placeholder="Optional"
            className={FIELD}
            inputClassName={INPUT}
            type="tel"
            inputMode="tel"
            helper="If you add it, the gate texts the PIN to the driver."
            value={driverPhone}
            onChange={(event) => setDriverPhone(event.target.value)}
          />
          <PropertyField properties={properties} value={locationId} onChange={setLocationId} />
        </>
      ) : null}

      <CourierSheet
        open={searchOpen}
        onOpenChange={setSearchOpen}
        locationId={locationId}
        onPick={(name) => {
          setCourier(name);
          setSearchOpen(false);
        }}
      />
    </BookingSteps>
  );
}
